import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { IMAGES_BY_COMPANY } from '~/constants/images';

import { storage as localStorage } from '../stores/LocalStorage';

import { CeriseConfig } from './configs/Carrefour';
import { CircleKConfig } from './configs/CircleK';
import { CityDrinksConfig } from './configs/CityDrinks';
import { DubaiExpressConfig } from './configs/DubaiExpress';
import { DubaiExpressComConfig } from './configs/DubaiExpressCom';
import { HeimkaupConfig } from './configs/Heimkaup';
import { JiffyConfig } from './configs/Jiffy';
import { KidsConfig } from './configs/Kids';
import { LocaleeConfig } from './configs/Localee';
import { MCosmeticConfig } from './configs/MCosmetic';
import { OcadoConfig } from './configs/Ocado';
import { OkieConfig } from './configs/Okie';
import { OskuhusConfig } from './configs/Oskuhus';
import { StorrdConfig } from './configs/Storrd';
import { ViloConfig } from './configs/Vilo';

import {
  type CompanyConfig,
  type CompanyFeatures,
  CompanyName,
  type EnvVariables,
} from './interface';

export const DEFAULT_COMPANIES = [
  CompanyName.Localee,
  CompanyName.CircleK,
  CompanyName.Vilo,
  CompanyName.Jiffy,
  CompanyName.Kids,
  CompanyName.Okie,
  CompanyName.Oskuhus,
  CompanyName.Heimkaup,
  CompanyName.Cerise,
];

class Company implements CompanyFeatures {
  private currentCompany: CompanyName = CompanyName.CityDrinks;
  private isOriginalCompany: boolean = true;
  private variables: EnvVariables = {};
  private readonly configs: Record<CompanyName, CompanyConfig> = {
    [CompanyName.CityDrinks]: CityDrinksConfig,
    [CompanyName.Localee]: LocaleeConfig,
    [CompanyName.CircleK]: CircleKConfig,
    [CompanyName.Vilo]: ViloConfig,
    [CompanyName.Jiffy]: JiffyConfig,
    [CompanyName.Kids]: KidsConfig,
    [CompanyName.Okie]: OkieConfig,
    [CompanyName.Oskuhus]: OskuhusConfig,
    [CompanyName.Heimkaup]: HeimkaupConfig,
    [CompanyName.Cerise]: CeriseConfig,
    [CompanyName.DubaiExpress]: DubaiExpressConfig,
    [CompanyName.DubaiExpressCom]: DubaiExpressComConfig,
    [CompanyName.Storrd]: StorrdConfig,
    [CompanyName.MCosmetic]: MCosmeticConfig,
    [CompanyName.Ocado]: OcadoConfig,
  };

  isActiveCaptchaDevMode = false;

  constructor() {
    makeAutoObservable(this);

    makePersistable(this, {
      name: 'Company',
      properties: ['isActiveCaptchaDevMode'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));

    if (import.meta.env.SSR) {
      return;
    }

    // In Storybook environment, __INITIAL_STATE__ might not be available yet
    // when Company constructor is called, so we skip initialization.
    // Storybook decorators will call setCompanyName() later.
    const isStorybook =
      typeof window !== 'undefined' &&
      document.getElementById('storybook-root') !== null;

    if (isStorybook && !window.__INITIAL_STATE__?.context) {
      return;
    }

    const contextData = window.__INITIAL_STATE__.context;

    if (import.meta.env.REACT_APP_ENV !== 'production') {
      this.setIsOriginalCompanyLogic(!!contextData.isOriginalLogic);
    }

    this.setCompanyName(contextData.company);
    this.setVariables(contextData.variables as EnvVariables);

    const rootDiv = document && document?.getElementById('app');
    rootDiv?.setAttribute('data-company', `${contextData.company}`);
  }

  setCompanyName(name: CompanyName) {
    this.currentCompany = name;
  }

  setVariables(variables: EnvVariables) {
    this.variables = variables;
  }

  setIsOriginalCompanyLogic(yes: boolean) {
    this.isOriginalCompany = yes;
  }

  setIsActiveCaptchaDevMode(active: boolean) {
    this.isActiveCaptchaDevMode = active;
  }

  get config(): CompanyConfig {
    if (this.isOriginalCompany) {
      return this.configs[this.currentCompany];
    } else {
      const company = { ...this.configs[this.currentCompany] };
      //**Use default company id LOCALEE if we check only UI and no other logic! */
      company.id = 'd05f30ba-ceb5-4633-a3b6-aa0f9463954f';

      company.warehouse = {
        default: 'NJSB1',
        minimalOrderFeeWarehouseCodes: [],
        location: { lat: 40.8537291, lng: -74.0197964 },
      };

      company.map = {
        center: { lat: 40.89753813649296, lng: -74.09670413033002 },
        zoom: 12,
      };

      return company;
    }
  }

  get name(): CompanyName {
    return this.currentCompany;
  }

  get envVariables(): EnvVariables {
    return this.variables;
  }

  get images() {
    return IMAGES_BY_COMPANY[this.currentCompany];
  }

  get currency() {
    return this.config.currency;
  }

  get currencyCode() {
    return this.config.currencyCode;
  }

  get isMobileMoneyPaymentEnabled(): boolean {
    return this.config.availablePaymentMethods?.includes('pvit') ?? false;
  }

  get isDisableLoyaltyProgram(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isExceptionAlcohol(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get hasAddress2(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isTrimTrailingZeros(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get hasWelcomeBlock(): boolean {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isAvailableCartInMobileHeader(): boolean {
    return !this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showETAInfoMobileHeader(): boolean {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showPersonalDataButtonMobileCabinetPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showLocationIconMobileCabinetPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showSectionsMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showSearchHeaderProductPage(): boolean {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showShowShareButtonsProductPage(): boolean {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showActionButtonsMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showShortReviewListMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showDescriptionReviewProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get customMobileRatingProductPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showExpressBadgeOnProductCards() {
    return !this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Heimkaup]);
  }

  get hideAboutUs() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get nonCloseableNavMobileShopPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showCardDisclaimer() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasWoltDeliveryProvider() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get hasDeepCatalog() {
    return [CompanyName.MCosmetic].includes(this.currentCompany);
  }

  get showCategorySEOBlock() {
    return !this.isDubaiExpressCompany;
  }

  get promocodeInInputResetCheckout() {
    return this.isIncludedIn([
      CompanyName.Localee,
      CompanyName.Oskuhus,
      CompanyName.CityDrinks,
    ]);
  }

  get loyaltyProgramEnabled() {
    return this.isIncludedIn([
      CompanyName.Jiffy,
      CompanyName.Localee,
      CompanyName.Cerise,
      CompanyName.Heimkaup,
      CompanyName.Ocado,
    ]);
  }

  get buttonOutPanelCheckout() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get needVerification() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showButtonInTitleAddress() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showCartMobileSubtotal() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showMobileDowloadPanel() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showExpressDeliveryBannerMainPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get useAwsPaymentMethod() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isSommelierEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isDeliveryInfoModalEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCompanyLogoOnMainMobile() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showAdditionalTextEmptyPayments() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  get deliveryMethodSelectionAvailable() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get isShareBlockCaptionVisible() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isShareBlockButtonIconVisible() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  get needKycCheck() {
    return this.match({
      citydrinks: true,
      localee: false,
      vilo: false,
      jiffy: false,
      ourkids: false,
      okie: false,
      oskuhus: false,
      heimkaup: false,
      default: false,
    });
  }

  get hasOnboarding() {
    return !this.isIncludedIn([CompanyName.Cerise]);
  }

  get hasOnboardingAutoplay() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasOnboardingCloseButton() {
    return !this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  get hasOnboardingDotsInHeader() {
    return !this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  get hasNewOnboardingDesign() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
    ]);
  }

  get hasOnboardingDotsAboveButtons() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showStartShopping() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCartModalCount() {
    return this.isIncludedIn([CompanyName.Localee, CompanyName.CircleK]);
  }

  get showCartAdditionalTextMobile() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get missAgeRestrictionModal() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Vilo,
      CompanyName.Kids,
      CompanyName.Okie,
      CompanyName.Oskuhus,
      CompanyName.Cerise,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
    ]);
  }

  get hideReviews() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Cerise,
      CompanyName.Okie,
      CompanyName.CityDrinks,
      CompanyName.Heimkaup,
    ]);
  }

  get hideFreeDeliveryProgressBar() {
    return false; // TODO: uncomment - this.currentCompany === CompanyName.CityDrinks;
  }

  get showCartCoutToRemove() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get addressModalDefault() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get combineItemsByDefaut() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isDefaultCompany() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showPartiallyPickedInfo() {
    return !this.isIncludedIn([CompanyName.Vilo]);
  }

  get hideFreezeMoneyMessage() {
    return this.isIncludedIn([CompanyName.Vilo]);
  }

  get hideDownloadAppPanel() {
    return this.isIncludedIn([CompanyName.CircleK, CompanyName.Kids]);
  }

  get isEnabledCaptcha() {
    return (
      import.meta.env.REACT_APP_ENV === 'production' ||
      this.isActiveCaptchaDevMode
    );
  }

  get useBarcode() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get hideOrderMap() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  get isReviewPanelHidden() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get showLinkedinIcon() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isEnabledIndexNow() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isFavsInCabinet() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showMatchingProducts_ProductPage() {
    return this.isIncludedIn([]);
  }

  get showMatchingProducts_CartPage() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.MCosmetic,
      ...DEFAULT_COMPANIES,
    ]);
  }

  get showSimilarProducts_ProductPage() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.MCosmetic,
      CompanyName.Jiffy,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
    ]);
  }

  get showFrequentlyBoughtTogether_ProductPage() {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  get isDefaultHeaderInCatalog() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledIconOnAddToCartButton() {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  get hasNewCheckoutElemtnts() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
    ]);
  }

  get isVisibleHeaderCategories() {
    return !this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isExpressDeliveryOffByDefault() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get canDefaultAddressBeEmpty() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showNewAddressModal() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.MCosmetic]);
  }

  get isPudoOnly() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  get showAddressSubtitle() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasContactSupport() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Okie]);
  }

  get hasAddressInFooter() {
    return this.config.address && this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showWorkingHoursInFooter() {
    return (
      this.config.workingHours && this.isIncludedIn([CompanyName.Heimkaup])
    );
  }

  get tileSizeEnabled() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledOutOfZoneModal() {
    return true;
  }

  get uiVariant() {
    return this.config.uiVariant ?? 'default';
  }

  get isMarketplaceVariant() {
    return this.uiVariant === 'marketplace';
  }

  get isDubaiExpressCompany() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  /** Hides cabinet (personal area) routes for marketplace companies */
  get hideCabinetRoutes() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Forces specific UI language regardless of user preference */
  get forceLanguage(): string | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'ru' });
  }

  /** Uses company default language instead of browser/cookie language */
  get preferCompanyLanguage() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  /** Uses marketplace-style product page layout */
  get useMarketplaceProductLayout() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get useVerticalProductImageSlider() {
    return this.isIncludedIn([CompanyName.DubaiExpress, CompanyName.MCosmetic]);
  }

  get useReviewsOnProductPage() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.MCosmetic,
      CompanyName.Ocado,
    ]);
  }

  /** Falls back to default warehouse when product has no warehouse */
  get useDefaultWarehouseWhenMissing() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Displays prices as integers without decimal places */
  get useIntegerCurrency() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** External URL for cart redirect instead of in-app cart */
  get externalCartUrl(): string | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'https://ozon.ru' });
  }

  /** Translation key for external cart button label */
  get externalCartLabelKey(): string | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'buyOnOzon' });
  }

  /** Shows feedback widget inside products grid on catalog page */
  get insertGridFeedbackInProductsGrid() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get showHeaderExpressBanner() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCheckoutAddressRestrictionBanner() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /** Controls orders icon/dropdown visibility in HeaderV3 */
  get showHeaderOrders() {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Controls delivery address display in HeaderV3 */
  get showHeaderAddress() {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Controls user action buttons (login/cabinet/cart) in HeaderV3 */
  get showHeaderUserActions() {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Controls promotional banners section in HeaderV3 */
  get showHeaderBanners() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Product card size variant for L breakpoint */
  get productCardVariantL(): 'l' | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'l' as const });
  }

  /** Product card size variant for XL breakpoint */
  get productCardVariantXL(): 'xl' | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'xl' as const });
  }

  /** Product card variant used in recommendation sliders */
  get recommendationsProductCardVariant() {
    return this.productCardVariantL;
  }

  get newDesignSystemEnabled() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
      CompanyName.Ocado,
    ]);
  }

  get isUpdateRequiredModalEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showOrderAgainSlider() {
    return (
      this.newDesignSystemEnabled && this.isIncludedIn([CompanyName.CityDrinks])
    );
  }

  get showTopPicksSlider() {
    return (
      this.newDesignSystemEnabled && this.isIncludedIn([CompanyName.CityDrinks])
    );
  }

  get hasAgeRestriction() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isCountryCodeLocked() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showProductRecommendations() {
    return this.isIncludedIn([
      CompanyName.Cerise,
      CompanyName.Heimkaup,
      CompanyName.Jiffy,
    ]);
  }

  get showProductImageZoom() {
    return !this.isIncludedIn([
      CompanyName.Heimkaup,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  get hideVolumeTitleOnProductPage() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isBuildingOptional() {
    return this.isIncludedIn([CompanyName.Heimkaup, CompanyName.MCosmetic]);
  }

  get pickupAddress() {
    return this.config.pickup ?? null;
  }

  get zonePolygonHidden() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Heimkaup]);
  }

  isCompanyNameExist(name: string): boolean {
    return this.configs[name as CompanyName] !== undefined;
  }

  isIncludedIn(nameList: string[]): boolean {
    return nameList.includes(this.currentCompany);
  }

  // Return type: T if passed all companies or default, else T | undefined
  // Priority: exact company > v3 (if newDesignSystemEnabled) > default
  match<T>(record: { default?: T } & Record<CompanyName, T>): T;
  match<T>(record: { default: T; v3?: T } & Partial<Record<CompanyName, T>>): T;
  match<T>(
    record: { default?: T; v3?: T } & Partial<Record<CompanyName, T>>,
  ): T | undefined;
  match<T>(
    record: { default?: T; v3?: T } & Partial<Record<CompanyName, T>>,
  ): T | undefined {
    if (this.currentCompany in record) {
      return record[this.currentCompany];
    }
    if ('v3' in record && this.newDesignSystemEnabled) {
      return record.v3;
    }
    return record.default;
  }

  // Return type: T if passed all design variants or default, else T | undefined
  variant<T>(record: { default?: T } & Record<'1' | '2', T>): T;
  variant<T>(record: { default: T } & Partial<Record<'1' | '2', T>>): T;
  variant<T>(record: Partial<Record<'1' | '2' | 'default', T>>): T | undefined;
  variant<T>(
    record: { default?: T } & Partial<Record<'1' | '2', T>>,
  ): T | undefined {
    return this.isIncludedIn([CompanyName.CityDrinks])
      ? record['1']
      : record['2'];
  }

  is(name: `${CompanyName}`): boolean {
    return this.name === name;
  }
}

export const company = new Company();
